import { createTableColumn, SelectionItemId, Table, TableCell, TableColumnDefinition, TableColumnSizingOptions, TableHeader, TableHeaderCell, TableRow, TableRowId, TableSelectionCell, useTableColumnSizing_unstable, useTableFeatures, useTableSelection } from "@fluentui/react-components";
import React from "react";
import { ScenarioResultModel, ScenariosListQuery } from "../../../swagger-clients/excel-runner-api-clients.service";
import { useLoading } from "../../../utils/loading-indicator.component";
import { TableBodyWithLoading } from "s365-dashboard-v2-file-picker";
import { JobProgressCount } from "../../../components/job-progress-count/job-progress-count.component";
import ShowLocalTime from "../../../components/show-local-time/show-local-time.component";
import { GetAnalysisStatus, GetProcessingStatus } from "../../../excel-runner/utilities/scenario.helpers";
import { getScenariosClient } from "../../../services/excel-runner.service";
import { FileTableItem } from "../../models/file-table.models";
import { getUserToken } from "../../../services/dashboard.service";
import { _copyAndSort } from "../../../utils/helpers/array.helpers";
import { processServerError } from "../../../utils/helpers/error.helper";
import { ApplicationSelectionModel } from "./copy-file-objects-dialog.component";

type SelectDoeExperimentsTableProps = {
    file: FileTableItem;
    //can use this to show number of items that are loaded
    onDataLoaded: (count: number) => void;
    onSelectedItemsChanged: (selectedItems: ApplicationSelectionModel[]) => void;
    selectedItems: number[];
}



export const SelectDoeExperimentsTable: React.FC<SelectDoeExperimentsTableProps> = (props) => {
    const [items, setItems] = React.useState<ScenarioResultModel[]>([]);
    const [isLoading, loadingService] = useLoading();
    const [columns, setColumns] = React.useState<TableColumnDefinition<ScenarioResultModel>[]>([]);
    const [selectedRows, setSelectedRows] = React.useState(
        () => new Set<TableRowId>(props.selectedItems)
    );
    React.useEffect(() => { getScenarios(); }, []);


    const [columnSizingOptions] = React.useState<TableColumnSizingOptions>({
        checkbox: {
            minWidth: 30,
            idealWidth: 30,
            autoFitColumns: false
        },
        id: {
            minWidth: 70,
            defaultWidth:70,
            idealWidth: 70,
            autoFitColumns: false
        },
        name: {
            minWidth: 100,
            defaultWidth: 9000,
        },
        analysisStatus: {
            minWidth: 100,
            defaultWidth: 200
        },
        processingStatus: {
            minWidth: 100,
            defaultWidth: 200
        },
        jobs: {
            minWidth: 100,
            defaultWidth: 200

        },
        created: {
            minWidth: 90,
            defaultWidth: 200
        }
    });


    const { getRows, columnSizing_unstable, tableRef, selection: {
        toggleRow,
        selectRow,
        clearRows,
        toggleAllRows
    }, } = useTableFeatures<ScenarioResultModel>(
        {
            columns,
            items,
            getRowId: (item: ScenarioResultModel) => (+item.id)

        },
        [useTableColumnSizing_unstable({ columnSizingOptions }),
        useTableSelection({
            selectionMode: "multiselect",
            selectedItems: selectedRows,
            onSelectionChange: (e, data) => {
                setSelectedRows(new Set<TableRowId>(data.selectedItems));
                const selectedIds = [...data.selectedItems].map(x => +x);
                const mappedSelections = selectedIds?.map(id => {
                    const experiment = items.find(x => x.id == id);
                    return { id: +experiment.id, filterId: experiment.filterId, filterVersionId: experiment.filterVersionId } as ApplicationSelectionModel;
                });
                props.onSelectedItemsChanged(mappedSelections);
            },
        })
        ]
    );
    const isRowSelected = (rowId: number) => {
        return selectedRows.has(rowId);
    }

    const rows = getRows((row) => {
        const selected = isRowSelected(+row.item.id);
        return {
            ...row,
            rowId: +row.item.id,
            selected
        };
    });
    React.useEffect(() => {
        const columnsLocal = getAllColumns();
        setColumns(columnsLocal);
    }, [selectedRows, items]);

    const getAllColumns = () => {

        const allRowsSelected = items.length > 0 && [...selectedRows].length > 0 && [...selectedRows].length == items.length;
        const someRowsSelected = items.length > 0 && [...selectedRows].length > 0 && !allRowsSelected;

        let columnsLocal: TableColumnDefinition<ScenarioResultModel>[] = [
            createTableColumn<ScenarioResultModel>({
                columnId: "checkbox",
                renderHeaderCell: () => {
                    return <TableSelectionCell
                        checked={
                            allRowsSelected ? true : someRowsSelected ? "mixed" : false
                        }
                        onClick={(ev) => onToggleAllRows(ev)}
                        checkboxIndicator={{ "aria-label": "Select all rows " }}
                    />
                },
                renderCell: (item) => {
                    const selected = isRowSelected(+item.id);

                    return <TableSelectionCell

                        checked={selected}
                        onClick={(ev) => { ev.stopPropagation(); toggleRow(ev, +item.id); }}
                        {...columnSizing_unstable.getTableCellProps("checkbox")}
                    />
                }
            }),
            createTableColumn<ScenarioResultModel>({
                columnId: "id",
                renderHeaderCell: () => <>ID</>,
                renderCell: (item: ScenarioResultModel) => {
                    return <span>{item.id}</span>;

                }
            }),
            createTableColumn<ScenarioResultModel>({
                columnId: "name",
                renderHeaderCell: () => <>Name</>,
                renderCell: (item: ScenarioResultModel) => {
                    return <span>{item.name}</span>;

                }
            }),
            createTableColumn<ScenarioResultModel>({
                columnId: "analysisStatus",
                renderHeaderCell: () => <>Analysis Status</>,
                renderCell: (item: ScenarioResultModel) => {
                    return GetAnalysisStatus(item.analysisStatus);
                }
            }),
            createTableColumn<ScenarioResultModel>({
                columnId: "processingStatus",
                renderHeaderCell: () => <>Processing Status</>,
                renderCell: (item: ScenarioResultModel) => {
                    return GetProcessingStatus(item.processingStatus);
                }
            }),
            createTableColumn<ScenarioResultModel>({
                columnId: "jobs",
                renderHeaderCell: () => <>Jobs</>,
                renderCell: (item: ScenarioResultModel) => {

                    return <JobProgressCount totalJobs={item.totalRows} calculatedJobs={item.calculatedRows} failedJobs={item.failedRows} />;

                }
            }),
            createTableColumn<ScenarioResultModel>({
                columnId: "created",
                renderHeaderCell: () => <>Created</>,
                renderCell: (item: ScenarioResultModel) => {

                    return <ShowLocalTime date={item.createdAt} />;
                }
            })
        ];

        return columnsLocal;
    }



    const isColumnCentered = (columnId: string) => {

        switch (columnId) {
            case "analysisStatus": return true;
            case "processingStatus": return true;
            case "jobs": return true;
            case "created": return true;

            default: return false;
        }
    }


    const getScenarios = () => {
        loadingService.showLoading("Getting Design of Experiments...", async (hideMessage) => {
            try {
                const client = getScenariosClient();
                const accessToken: string = await getUserToken();
                const query = { siteId: "s365v2", accessToken: accessToken, driveItemId: props.file.uniqueIdentifier, flowsheetsListId: "s365v2" } as ScenariosListQuery;
                const scenariosResp = await client.getScenarios(query);
                let scenariosSorted = _copyAndSort<ScenarioResultModel>(scenariosResp, "id", true);
                scenariosSorted = scenariosSorted.filter(x => !x.isDeleted);
                setItems(scenariosSorted ?? []);
                props.onDataLoaded(scenariosSorted?.length ?? 0);

            } catch (error) {
                processServerError(error, undefined, "An error occurred while trying to get experiments.");
            } finally {
                hideMessage();
            }
        });
    }

    const onToggleAllRows = (ev: any) => {
        toggleAllRows(ev);
    }

    return <div>
        <div className='scenarios-table__wrapper'>
            <Table ref={tableRef} as="table" {...columnSizing_unstable.getTableProps()} >
                <TableHeader>
                    <TableRow>
                        {columns.map((column) => (
                            <TableHeaderCell
                                onDragStart={e => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                }}
                                className={`${isColumnCentered(column.columnId as string) ? 'files-table__cell--center' : ''} files-table__cell--bold`}
                                {...columnSizing_unstable.getTableHeaderCellProps(
                                    column.columnId
                                )}
                            >
                                {column.renderHeaderCell()}
                            </TableHeaderCell>
                        ))}
                    </TableRow>
                </TableHeader>
                <TableBodyWithLoading isLoading={isLoading}
                    columnCount={7} loadingMessage="Loading..."
                    itemCount={items ? items.length : 0}
                    noItemsMessage="No items found.">
                    {!isLoading && rows.map(({ item, selected }) => (
                        <TableRow
                            key={`scenario-${item.id}`}
                            style={{ cursor: "pointer" }}
                            onClick={() => { toggleRow(null, +item.id); }}
                        >

                            {columns.map((column) => (
                                <TableCell
                                    className={`${isColumnCentered(column.columnId as string) ? ' column--center' : ''}`}
                                    {...columnSizing_unstable.getTableCellProps(column.columnId)}
                                >
                                    {column.renderCell(item)}
                                </TableCell>
                            ))}

                        </TableRow>
                    ))}


                </TableBodyWithLoading>
            </Table>
        </div>
    </div>
}

